import { IExcelReportData, ITableData } from 'types/excelExport';
import { IProFormaGeneralInfo, IOperatingAndPermanentDebtInfo } from 'views/ProFormaTable/types';
import { ProFormaModelIds } from 'constants/proForma';
import {
    createHeaderRow,
    createDataRow,
    createBlankRow,
    createLabelValueRow,
    formatDate,
    formatMonths,
    formatNumber,
    parseNumber,
} from './helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from './constants';
import { getModelExportConfig } from './modelConfig';

interface IBuildingInfoSheetParams {
    proForma: IProFormaGeneralInfo;
    operatingInfo: IOperatingAndPermanentDebtInfo;
    totalRentableSf?: number;
}

export const formatBuildingInfoSheet = ({
    proForma,
    operatingInfo,
    totalRentableSf,
}: IBuildingInfoSheetParams): IExcelReportData => {
    const config = getModelExportConfig(proForma.proFormaModelId);
    const { buildingInfoTab } = config;

    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const isApartmentWithRetail = proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const baseGrossSF = parseNumber(proForma.builtGrossSquareFootage) || 0;
    const retailGrossSF = parseNumber(proForma.retailBuiltGrossSquareFootage) || 0;
    const grossSF = isApartmentWithRetail ? baseGrossSF + retailGrossSF : baseGrossSF;
    const efficiencyRatio = parseNumber(proForma.efficiencyRatio);

    const topHeaderRows: ITableData[] = [
        createDataRow([{
            value: proForma.dealName || proForma.name,
            cellFont: { bold: true, size: 14 },
            width: 30,
        }]),
        createDataRow([{ value: 'Building Information', cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(2),
    ];

    // Property Information
    const propertyInfoRows: ITableData[] = [
        createHeaderRow(['PROPERTY INFORMATION', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(buildingInfoTab.propertyInfo.dealName, proForma.dealName || proForma.name, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.address, proForma.address, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.city, proForma.city, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.state, proForma.state, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.zip, proForma.zipCode, true),
        createLabelValueRow(buildingInfoTab.propertyInfo.county, proForma.county, true),
        createBlankRow(2),
    ];

    // Building Specifications
    const buildingSpecRows: ITableData[] = [
        createHeaderRow(['BUILDING SPECIFICATIONS', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.numberOfBuildings, proForma.buildings, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.averageStories, proForma.averageStories, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.grossSF, grossSF, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.rentableSF, totalRentableSf, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.buildingSpecs.efficiencyRatio, efficiencyRatio, true, EXCEL_NUM_FORMATS.PERCENT),
    ];

    if (buildingInfoTab.buildingSpecs.totalResidentialUnits) {
        buildingSpecRows.push(createLabelValueRow(
            buildingInfoTab.buildingSpecs.totalResidentialUnits,
            proForma.estimatedUnits,
            true,
            EXCEL_NUM_FORMATS.NUMBER,
        ));
    }

    buildingSpecRows.push(createBlankRow(2));

    // Parking Section
    const surfaceSpaces = proForma.surfaceParkingSpaces || 0;
    const structuredSpaces = proForma.structuredParkingSpaces || 0;
    const undergroundSpaces = proForma.undergroundParkingSpaces || 0;
    const trailerSpaces = proForma.trailerParkingSpaces || 0;
    const totalParkingSpaces = surfaceSpaces + structuredSpaces + undergroundSpaces + trailerSpaces;

    const parkingRows: ITableData[] = [
        createHeaderRow(['PARKING', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(buildingInfoTab.parking.surfaceSpaces, surfaceSpaces, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.parking.structuredSpaces, structuredSpaces, true, EXCEL_NUM_FORMATS.NUMBER),
        createLabelValueRow(buildingInfoTab.parking.undergroundSpaces, undergroundSpaces, true, EXCEL_NUM_FORMATS.NUMBER),
    ];

    if (buildingInfoTab.parking.trailerSpaces) {
        parkingRows.push(createLabelValueRow(buildingInfoTab.parking.trailerSpaces, trailerSpaces, true, EXCEL_NUM_FORMATS.NUMBER));
    }

    if (buildingInfoTab.parking.parkingRatio && proForma.estimatedUnits) {
        const parkingRatio = `${formatNumber(totalParkingSpaces / proForma.estimatedUnits, 1)} ${buildingInfoTab.parking.ratioLabel}`;
        parkingRows.push(createLabelValueRow(buildingInfoTab.parking.parkingRatio, parkingRatio, true));
    }

    parkingRows.push(createBlankRow(2));

    // Key Dates
    const keyDatesRows: ITableData[] = [
        createHeaderRow(['KEY DATES', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(buildingInfoTab.keyDates.analysisStart, formatDate(proForma.startDate), true),
        createLabelValueRow(buildingInfoTab.keyDates.constructionStart, formatDate(proForma.constructionStart), true),
        createLabelValueRow(buildingInfoTab.keyDates.constructionDuration, formatMonths(proForma.constructionDuration), true),
        createLabelValueRow(buildingInfoTab.keyDates.operationsStart, formatDate(operatingInfo?.operationStartDate), true),
        createLabelValueRow(buildingInfoTab.keyDates.holdPeriod, formatMonths(proForma.holdPeriod || 0), true),
        createLabelValueRow(buildingInfoTab.keyDates.projectedSaleDate, formatDate(proForma.saleDate), true),
        createBlankRow(2),
    ];

    // Operating Assumptions
    const operatingAssumptionsRows: ITableData[] = [
        createHeaderRow(['OPERATING ASSUMPTIONS', ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(
            buildingInfoTab.operatingAssumptions.operationStartDate,
            formatDate(operatingInfo?.operationStartDate),
            true,
        ),
        createLabelValueRow(
            buildingInfoTab.operatingAssumptions.occupancyUponStabilization,
            parseNumber(operatingInfo?.occupancyUponStabilizationPercentage),
            true,
            EXCEL_NUM_FORMATS.PERCENT,
        ),
        createLabelValueRow(
            buildingInfoTab.operatingAssumptions.rentGrowthAnnual,
            parseNumber(operatingInfo?.rentalGrowthPercentage),
            true,
            EXCEL_NUM_FORMATS.PERCENT,
        ),
    ];

    if (buildingInfoTab.operatingAssumptions.percentPreLeased) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.percentPreLeased,
            parseNumber(operatingInfo?.percentPreLeased),
            true,
            EXCEL_NUM_FORMATS.PERCENT,
        ));
    }

    if (buildingInfoTab.operatingAssumptions.leaseUpPaceUnitsPerMonth) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.leaseUpPaceUnitsPerMonth,
            operatingInfo?.effectiveLeaseUpPace,
            true,
        ));
    }

    operatingAssumptionsRows.push(createLabelValueRow(
        buildingInfoTab.operatingAssumptions.lengthOfLease,
        formatMonths(operatingInfo?.lengthOfLease || 12),
        true,
    ));

    if (buildingInfoTab.operatingAssumptions.initialFreeRent) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.initialFreeRent,
            formatMonths(operatingInfo?.initialFreeRent || 0),
            true,
        ));
    }

    if (buildingInfoTab.operatingAssumptions.stabilizedFreeRent) {
        operatingAssumptionsRows.push(createLabelValueRow(
            buildingInfoTab.operatingAssumptions.stabilizedFreeRent,
            formatMonths(operatingInfo?.stabilizedFreeRent || 0),
            true,
        ));
    }

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [],
                tableData: [
                    ...propertyInfoRows,
                    ...buildingSpecRows,
                    ...parkingRows,
                    ...keyDatesRows,
                    ...operatingAssumptionsRows,
                ],
            },
        ],
    };
};
